import { clsx } from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getStoreBasicApi,
  GetStoreBasicRes,
} from "../../apis/store/get-store-basic.api";
import { useRequest } from "../../lib/request/use-request";
import * as styles from "./index.module.scss";

interface StoreDropdownProps {
  activeId?: string;
  onSelect?: (id: string) => void;
}

export function StoreDropdown({ activeId, onSelect }: StoreDropdownProps = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { data, requestHandler: fetchStores } = useRequest<GetStoreBasicRes>({
    api: getStoreBasicApi,
    title: "获取店铺",
  });

  useEffect(() => {
    void fetchStores();
  }, [fetchStores]);

  useEffect(() => {
    const stores = data?.dataList;
    if (!stores || stores.length === 0) return;
    if (activeId && stores.some((s) => s.id === activeId)) return;
    const defaultStore = stores.find((s) => s.isDefault) ?? stores[0];
    onSelect?.(defaultStore.id);
  }, [data, activeId, onSelect]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = useCallback(
    (id: string) => {
      setIsOpen(false);
      onSelect?.(id);
    },
    [onSelect],
  );

  const activeLabel = useMemo(() => {
    const match = data?.dataList.find((s) => s.id === activeId);
    return match?.name ?? "";
  }, [activeId, data]);

  return (
    <div className={styles.storeDropdown} ref={containerRef}>
      <button
        type="button"
        className={styles.storeDropdownTrigger}
        onClick={() => setIsOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={styles.storeDropdownTriggerLabel}>{activeLabel}</span>
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          aria-hidden="true"
          focusable="false"
          className={clsx(
            styles.storeDropdownArrow,
            isOpen && styles.storeDropdownArrowOpen,
          )}
        >
          <path
            d="M6 9l6 6 6-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <ul className={styles.storeDropdownMenu} role="listbox">
          {data?.dataList.map((store) => {
            const isActive = store.id === activeId;
            return (
              <li key={store.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={clsx(styles.storeDropdownItem, {
                    [styles.storeDropdownItemActive]: isActive,
                  })}
                  onClick={() => handleSelect(store.id)}
                >
                  {store.name}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
